import { useEffect, useRef, useState } from "react";
import { CalendarDays, ClipboardList, Eye, FileText, GripVertical, Loader2, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

// Multi-day registration ("Lichtjestour"-style events) — admin section for
// EventDaysManager (lightweight day CRUD, no route/POI/map building — that stays
// StapKompas-only in AdminEventCompanion.jsx's DaysManager/DayCard) and
// RegistrationFieldsManager (admin-configurable checkout form field builder).
// Both reuse the exact manager+row idiom already used for RouteGroupsManager/
// RouteGroupRow in AdminEventCompanion.jsx (inline add/edit, window.confirm on
// delete, onChanged bubbling to a shared parent fetch) — deliberately a fresh,
// small component tree rather than reusing DaysManager/DayCard, which are
// entangled with StapKompas concerns this feature must not depend on.

const FIELD_TYPE_OPTIONS = [
  ["text", "Tekst"],
  ["textarea", "Lange tekst"],
  ["number", "Nummer"],
  ["email", "E-mail"],
  ["phone", "Telefoon"],
  ["select", "Keuzelijst"],
  ["checkbox", "Checkbox (ja/nee)"],
];

function DayRow({ day, authAxios, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    day_number: day.day_number,
    date: day.date || "",
    time: day.time || "",
    label: day.label || "",
    capacity: day.capacity != null ? String(day.capacity) : "",
  });

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.patch(`/admin/event-days/${day.id}`, {
        day_number: Number(form.day_number) || day.day_number,
        date: form.date || null,
        time: form.time || null,
        label: form.label || null,
        capacity: form.capacity.trim() ? Number(form.capacity) : null,
      });
      toast.success("Dag opgeslagen");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Dag ${day.day_number} verwijderen? Aanmeldingen voor deze dag verdwijnen mee.`)) return;
    try {
      await authAxios.delete(`/admin/event-days/${day.id}`);
      toast.success("Dag verwijderd");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border" data-testid={`mdr-day-row-${day.id}`}>
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-white font-semibold">Dag {day.day_number}</span>
          {day.label && <span className="text-xs text-slate-300">{day.label}</span>}
          {day.date && <span className="text-xs text-svj-cyan">{day.date}{day.time ? ` om ${day.time.slice(0, 5)}` : ""}</span>}
          {day.capacity != null && (
            <span
              className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold ${
                (day.registered_count || 0) >= day.capacity
                  ? "bg-red-500/20 text-red-300"
                  : "bg-svj-border text-slate-300"
              }`}
              data-testid={`mdr-day-occupancy-${day.id}`}
            >
              {(day.registered_count || 0) >= day.capacity ? "VOL" : `${day.registered_count || 0} / ${day.capacity}`}
            </span>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setEditing(true)} data-testid={`mdr-day-edit-${day.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">
            Bewerken
          </button>
          <button type="button" onClick={del} data-testid={`mdr-day-delete-${day.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2" data-testid={`mdr-day-editing-${day.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        <input value={form.day_number} onChange={(e) => setForm({ ...form, day_number: e.target.value })} type="number" placeholder="Dagnummer" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} type="time" data-testid={`mdr-day-time-${day.id}`} className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (bv. Vrijdag)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} type="number" min="0" placeholder="Capaciteit (optioneel)" data-testid={`mdr-day-capacity-${day.id}`} className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button type="button" onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

export function EventDaysManager({ eventId, authAxios, days, loading, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ day_number: "", date: "", time: "", label: "", capacity: "" });

  const create = async () => {
    const dayNumber = Number(form.day_number) || days.length + 1;
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/days`, {
        day_number: dayNumber,
        date: form.date || null,
        time: form.time || null,
        label: form.label || null,
        capacity: form.capacity.trim() ? Number(form.capacity) : null,
      });
      toast.success("Dag toegevoegd");
      setForm({ day_number: "", date: "", time: "", label: "", capacity: "" });
      setAdding(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Dagen</div>
          <span className="text-xs text-slate-400">({days.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="mdr-day-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Dag
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Elke dag hier kan apart geselecteerd worden bij aanmelding — een ticket kan zich voor één of meerdere dagen aanmelden.
        Vul optioneel een capaciteit in per dag; is die vol, dan wordt dit duidelijk getoond bij het afrekenen en in deze lijst.
      </p>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}
      {!loading && days.length === 0 && !adding && (
        <div className="text-sm text-slate-400">Nog geen dagen ingesteld voor dit meerdaagse event.</div>
      )}

      {adding && (
        <div className="mb-3 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            <input value={form.day_number} onChange={(e) => setForm({ ...form, day_number: e.target.value })} type="number" placeholder={`Dagnummer (bv. ${days.length + 1})`} data-testid="mdr-day-number" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" data-testid="mdr-day-date" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} type="time" data-testid="mdr-day-time" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (bv. Vrijdag)" data-testid="mdr-day-label" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} type="number" min="0" placeholder="Capaciteit (optioneel)" data-testid="mdr-day-capacity" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="mdr-day-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {days.map((d) => (
          <DayRow key={d.id} day={d} authAxios={authAxios} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function optionsToText(options) {
  return Array.isArray(options) ? options.join("\n") : "";
}

function textToOptions(text) {
  const lines = (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.length ? lines : null;
}

// Renders "…gelijk is aan" as the shape the chosen controlling field actually
// answers with -- a dropdown of its own options for a Keuzelijst, Ja/Nee for a
// checkbox, or a free-text input otherwise -- so an admin picks a value that can
// genuinely match rather than typing something that can never trigger.
function DependsOnValueInput({ controllerField, value, onChange, testid }) {
  if (controllerField?.field_type === "select" && (controllerField.options || []).length > 0) {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 h-9 w-full px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
        <option value="">Kies een waarde…</option>
        {controllerField.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  if (controllerField?.field_type === "checkbox") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 h-9 w-full px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
        <option value="">Kies…</option>
        <option value="true">Ja (aangevinkt)</option>
        <option value="false">Nee (niet aangevinkt)</option>
      </select>
    );
  }
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Exacte waarde" data-testid={testid} className="mt-1 h-9 w-full px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
  );
}

// The "Toon dit veld alleen als" block shared by both the add and edit forms
// below -- a dropdown offering either every OTHER field on this event (never
// itself, shown once a value is also chosen) or, for an event with timeslot
// categories, one of those categories directly -- the two are mutually
// exclusive per field (the backend rejects both set at once), so picking one
// option group always clears the other. `categories` is only non-empty for a
// timeslot-booking event with at least one rate card; omit/empty for every
// other event type, same as the checkout page's own convention.
function DependsOnFieldPicker({ otherFields, categories = [], form, setForm, testidPrefix }) {
  const controllerField = otherFields.find((f) => f.id === form.depends_on_field_id) || null;
  const selectValue = form.depends_on_timeslot_category_id
    ? `category:${form.depends_on_timeslot_category_id}`
    : form.depends_on_field_id
      ? `field:${form.depends_on_field_id}`
      : "";
  const handleChange = (raw) => {
    if (raw.startsWith("category:")) {
      setForm({ ...form, depends_on_timeslot_category_id: raw.slice("category:".length), depends_on_field_id: "", depends_on_value: "" });
    } else if (raw.startsWith("field:")) {
      setForm({ ...form, depends_on_field_id: raw.slice("field:".length), depends_on_value: "", depends_on_timeslot_category_id: "" });
    } else {
      setForm({ ...form, depends_on_field_id: "", depends_on_value: "", depends_on_timeslot_category_id: "" });
    }
  };
  return (
    <div className="space-y-1.5">
      <label className="block">
        <span className="text-xs text-slate-400">Toon dit veld alleen als (optioneel)</span>
        <select
          value={selectValue}
          onChange={(e) => handleChange(e.target.value)}
          data-testid={`${testidPrefix}-field`}
          className="mt-1 h-9 w-full px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
        >
          <option value="">Altijd tonen</option>
          {otherFields.length > 0 && (
            <optgroup label="Een ander veld heeft een waarde">
              {otherFields.map((f) => <option key={f.id} value={`field:${f.id}`}>{f.label}</option>)}
            </optgroup>
          )}
          {categories.length > 0 && (
            <optgroup label="Deze categorie is gekozen">
              {categories.map((c) => <option key={c.id} value={`category:${c.id}`}>{c.name}</option>)}
            </optgroup>
          )}
        </select>
      </label>
      {form.depends_on_field_id && (
        <label className="block">
          <span className="text-xs text-slate-400">…gelijk is aan</span>
          <DependsOnValueInput
            controllerField={controllerField}
            value={form.depends_on_value}
            onChange={(v) => setForm({ ...form, depends_on_value: v })}
            testid={`${testidPrefix}-value`}
          />
        </label>
      )}
      <p className="text-[11px] text-slate-500">
        Bijv.: toon dit veld alleen als &quot;Met hoeveel personen neem je deel?&quot; gelijk is aan &quot;Ik neem 1 bijrijder mee&quot;
        {categories.length > 0 && <>, of alleen als categorie &quot;{categories[0].name}&quot; is gekozen</>}.
      </p>
    </div>
  );
}

function RegistrationFieldRow({ field, allFields, categories = [], authAxios, onChanged, dragHandleProps, rowRef, isDragging, isDropTarget }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    key: field.key,
    label: field.label,
    field_type: field.field_type,
    required: !!field.required,
    options: optionsToText(field.options),
    sort_order: field.sort_order ?? 0,
    active: field.active !== false,
    is_participant_name: !!field.is_participant_name,
    depends_on_field_id: field.depends_on_field_id || "",
    depends_on_value: field.depends_on_value || "",
    depends_on_timeslot_category_id: field.depends_on_timeslot_category_id || "",
  });
  const otherFields = allFields.filter((f) => f.id !== field.id);

  const save = async () => {
    if (!form.label.trim()) return toast.error("Label is verplicht");
    if (form.depends_on_field_id && !form.depends_on_value.trim()) {
      return toast.error("Kies een waarde voor de voorwaarde, of laat het veld op 'Altijd tonen' staan");
    }
    setSaving(true);
    try {
      await authAxios.patch(`/admin/registration-fields/${field.id}`, {
        key: form.key.trim(),
        label: form.label.trim(),
        field_type: form.field_type,
        required: !!form.required,
        options: form.field_type === "select" ? textToOptions(form.options) : null,
        sort_order: Number(form.sort_order) || 0,
        active: !!form.active,
        is_participant_name: !!form.is_participant_name,
        depends_on_field_id: form.depends_on_field_id || null,
        depends_on_value: form.depends_on_field_id ? form.depends_on_value.trim() : null,
        depends_on_timeslot_category_id: form.depends_on_timeslot_category_id || null,
      });
      toast.success("Veld opgeslagen");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Veld "${field.label}" verwijderen?`)) return;
    try {
      await authAxios.delete(`/admin/registration-fields/${field.id}`);
      toast.success("Veld verwijderd");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (!editing) {
    return (
      <div
        ref={rowRef}
        className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border transition-colors ${
          isDropTarget ? "border-svj-cyan" : "border-svj-border"
        } ${isDragging ? "opacity-40" : ""}`}
        data-testid={`mdr-field-row-${field.id}`}
      >
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <span
            onPointerDown={dragHandleProps?.onPointerDown}
            onPointerMove={dragHandleProps?.onPointerMove}
            onPointerUp={dragHandleProps?.onPointerUp}
            onPointerCancel={dragHandleProps?.onPointerUp}
            data-testid={`mdr-field-drag-${field.id}`}
            title="Sleep om volgorde te wijzigen"
            // touch-none is required for the drag gesture to work on mobile at all --
            // without it, a touch-move on this handle scrolls the page instead of
            // being delivered to us as pointermove events.
            className="touch-none select-none cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 -ml-1"
          >
            <GripVertical className="h-4 w-4" />
          </span>
          <span className="text-sm text-white font-semibold">{field.label}</span>
          <span className="text-xs text-slate-400">({field.key})</span>
          <span className="text-[10px] uppercase tracking-widest bg-svj-border text-slate-300 rounded-full px-2 py-0.5">
            {FIELD_TYPE_OPTIONS.find(([k]) => k === field.field_type)?.[1] || field.field_type}
          </span>
          {field.required && <span className="text-[10px] uppercase tracking-widest bg-svj-orange/20 text-svj-orange rounded-full px-2 py-0.5">Verplicht</span>}
          {field.is_participant_name && (
            <span className="text-[10px] uppercase tracking-widest bg-svj-cyan/20 text-svj-cyan rounded-full px-2 py-0.5" data-testid={`mdr-field-participant-name-badge-${field.id}`}>
              Naam deelnemer
            </span>
          )}
          {field.active === false && <span className="text-[10px] uppercase tracking-widest bg-svj-border text-slate-400 rounded-full px-2 py-0.5">Inactief</span>}
          {field.depends_on_field_id && (
            <span className="text-[10px] uppercase tracking-widest bg-svj-purple/20 text-svj-purple rounded-full px-2 py-0.5" data-testid={`mdr-field-conditional-badge-${field.id}`}>
              Alleen als &quot;{allFields.find((f) => f.id === field.depends_on_field_id)?.label || "?"}&quot; = &quot;{field.depends_on_value}&quot;
            </span>
          )}
          {field.depends_on_timeslot_category_id && (
            <span className="text-[10px] uppercase tracking-widest bg-svj-purple/20 text-svj-purple rounded-full px-2 py-0.5" data-testid={`mdr-field-category-conditional-badge-${field.id}`}>
              Alleen bij categorie &quot;{categories.find((c) => c.id === field.depends_on_timeslot_category_id)?.name || "?"}&quot;
            </span>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setEditing(true)} data-testid={`mdr-field-edit-${field.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">
            Bewerken
          </button>
          <button type="button" onClick={del} data-testid={`mdr-field-delete-${field.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2" data-testid={`mdr-field-editing-${field.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="Sleutel (a-z, 0-9, - _)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <select value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })} className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
          {FIELD_TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} type="number" placeholder="Volgorde" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </div>
      {form.field_type === "select" && (
        <textarea value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder={"Opties, één per regel"} rows={3} className="w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      )}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
          <span className="text-xs text-slate-300">Verplicht</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
          <span className="text-xs text-slate-300">Actief (zichtbaar bij aanmelding)</span>
        </label>
      </div>
      <label className="flex items-start gap-2" data-testid={`mdr-field-participant-name-toggle-${field.id}`}>
        <input
          type="checkbox"
          checked={!!form.is_participant_name}
          onChange={(e) => setForm({ ...form, is_participant_name: e.target.checked })}
          className="mt-0.5 h-4 w-4 rounded border-svj-border bg-svj-bg"
        />
        <span className="text-xs text-slate-300">
          Dit veld bevat de naam van de deelnemer
          <span className="block text-slate-500">
            Wordt overgeslagen bij een bestelling van precies 1 ticket (naam komt dan automatisch uit de klantgegevens). Slechts één veld per event kan dit zijn — instellen hier haalt het bij een ander veld weg.
          </span>
        </span>
      </label>
      <DependsOnFieldPicker otherFields={otherFields} categories={categories} form={form} setForm={setForm} testidPrefix={`mdr-field-depends-on-${field.id}`} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button type="button" onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

export function RegistrationFieldsManager({ eventId, authAxios, fields, loading, onChanged, categories = [] }) {
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ key: "", label: "", field_type: "text", required: false, options: "", sort_order: 0, is_participant_name: false, depends_on_field_id: "", depends_on_value: "", depends_on_timeslot_category_id: "" });
  // Drag-to-reorder: the "Volgorde" number field alone made reordering several
  // fields tedious (renumbering each one by hand). This keeps a local, optimistic
  // copy of the list order during a drag and persists it as sequential sort_order
  // values on drop -- reported directly as hard to use in practice.
  //
  // Uses the Pointer Events API (not HTML5 drag-and-drop) specifically because
  // native HTML5 DnD never fires on touch devices at all -- an admin using this
  // on a phone found the handle completely unresponsive. Pointer Events unify
  // mouse/touch/pen: setPointerCapture on the handle keeps routing move/up events
  // to it regardless of where the finger/cursor physically ends up, so there's no
  // need for per-row dragover/drop handlers -- pointermove just measures every
  // row's current position (via rowRefs) and picks whichever one the pointer is
  // currently over.
  const [orderedFields, setOrderedFields] = useState(fields);
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const rowRefs = useRef({});
  const activePointerId = useRef(null);
  useEffect(() => {
    if (!reordering) setOrderedFields(fields);
  }, [fields, reordering]);

  const reorder = async (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const current = orderedFields;
    const from = current.findIndex((f) => f.id === sourceId);
    const to = current.findIndex((f) => f.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrderedFields(next);
    setReordering(true);
    try {
      await Promise.all(
        next
          .filter((f, idx) => f.sort_order !== idx)
          .map((f) =>
            authAxios.patch(`/admin/registration-fields/${f.id}`, { sort_order: next.indexOf(f) }),
          ),
      );
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      setOrderedFields(fields);
    } finally {
      setReordering(false);
    }
  };

  const findRowUnderPoint = (clientY) => {
    let closestId = null;
    let closestDist = Infinity;
    for (const [id, node] of Object.entries(rowRefs.current)) {
      if (!node) continue;
      const rect = node.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      const dist = Math.abs(mid - clientY);
      if (dist < closestDist) {
        closestDist = dist;
        closestId = id;
      }
    }
    return closestId;
  };

  const dragHandlersFor = (fieldId) => ({
    onPointerDown: (e) => {
      e.preventDefault();
      activePointerId.current = e.pointerId;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragId(fieldId);
      setDropId(fieldId);
    },
    onPointerMove: (e) => {
      if (activePointerId.current !== e.pointerId) return;
      const overId = findRowUnderPoint(e.clientY);
      if (overId) setDropId(overId);
    },
    onPointerUp: (e) => {
      if (activePointerId.current !== e.pointerId) return;
      activePointerId.current = null;
      const sourceId = fieldId;
      const targetId = dropId;
      setDragId(null);
      setDropId(null);
      reorder(sourceId, targetId);
    },
  });

  const create = async () => {
    if (!form.label.trim()) return toast.error("Label is verplicht");
    if (!form.key.trim()) return toast.error("Sleutel is verplicht");
    if (form.depends_on_field_id && !form.depends_on_value.trim()) {
      return toast.error("Kies een waarde voor de voorwaarde, of laat het veld op 'Altijd tonen' staan");
    }
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/registration-fields`, {
        key: form.key.trim(),
        label: form.label.trim(),
        field_type: form.field_type,
        required: !!form.required,
        options: form.field_type === "select" ? textToOptions(form.options) : null,
        sort_order: Number(form.sort_order) || 0,
        is_participant_name: !!form.is_participant_name,
        depends_on_field_id: form.depends_on_field_id || null,
        depends_on_value: form.depends_on_field_id ? form.depends_on_value.trim() : null,
        depends_on_timeslot_category_id: form.depends_on_timeslot_category_id || null,
      });
      toast.success("Veld toegevoegd");
      setForm({ key: "", label: "", field_type: "text", required: false, options: "", sort_order: 0, is_participant_name: false, depends_on_field_id: "", depends_on_value: "", depends_on_timeslot_category_id: "" });
      setAdding(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Aanmeldformulier</div>
          <span className="text-xs text-slate-400">({fields.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="mdr-field-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Veld
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Deze velden verschijnen per deelnemer/ticket op het aanmeldformulier bij het afrekenen, naast de
        dagkeuze hierboven. Geen velden ingesteld? Dan toont het formulier alleen de dagkeuze.
      </p>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}
      {!loading && fields.length === 0 && !adding && (
        <div className="text-sm text-slate-400">Nog geen extra velden voor dit aanmeldformulier.</div>
      )}

      {adding && (
        <div className="mb-3 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (bv. Allergie)" data-testid="mdr-field-label" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="Sleutel (bv. allergie)" data-testid="mdr-field-key" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <select value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })} data-testid="mdr-field-type" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
              {FIELD_TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} type="number" placeholder="Volgorde" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
          {form.field_type === "select" && (
            <textarea value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder={"Opties, één per regel"} rows={3} className="w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          )}
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
            <span className="text-xs text-slate-300">Verplicht veld</span>
          </label>
          <label className="flex items-start gap-2" data-testid="mdr-field-participant-name-new">
            <input
              type="checkbox"
              checked={!!form.is_participant_name}
              onChange={(e) => setForm({ ...form, is_participant_name: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-svj-border bg-svj-bg"
            />
            <span className="text-xs text-slate-300">
              Dit veld bevat de naam van de deelnemer
              <span className="block text-slate-500">
                Wordt overgeslagen bij een bestelling van precies 1 ticket (naam komt dan automatisch uit de klantgegevens).
              </span>
            </span>
          </label>
          <DependsOnFieldPicker otherFields={fields} categories={categories} form={form} setForm={setForm} testidPrefix="mdr-field-depends-on-new" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="mdr-field-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {orderedFields.map((f) => (
          <RegistrationFieldRow
            key={f.id}
            field={f}
            allFields={fields}
            categories={categories}
            authAxios={authAxios}
            onChanged={onChanged}
            isDragging={dragId === f.id}
            isDropTarget={dropId === f.id && dragId !== f.id}
            rowRef={(node) => {
              rowRefs.current[f.id] = node;
            }}
            dragHandleProps={dragHandlersFor(f.id)}
          />
        ))}
      </div>
    </div>
  );
}

// Deelnemersbrief — an admin-uploaded .docx mail-merge template (see
// backend/participant_letter.py), auto-rendered to a personalized PDF and
// attached to the confirmation email. Only shown for multi-day-registration
// events (the 3 recognized merge fields — name/registered days/start number —
// only make sense there). Unlike EventDaysManager/RegistrationFieldsManager
// above, this card doesn't do its own GET fetch for its enable-flag/template-
// url: the parent AdminEventForm.jsx already holds the full raw event row
// (rawEvent) from its own existing fetch, so those two fields are passed down
// as props instead of being fetched a second time; onChanged bubbles a patch
// back up so the parent's own state stays in sync after a toggle/upload/remove.
function ParticipantLetterManager({ eventId, authAxios, enabled, templateUrl, onChanged }) {
  const fileInputRef = useRef(null);
  const [toggling, setToggling] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const toggle = async () => {
    setToggling(true);
    try {
      await authAxios.patch(`/admin/events/${eventId}`, { participant_letter_enabled: !enabled });
      onChanged({ participant_letter_enabled: !enabled });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setToggling(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand te groot (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post(`/admin/events/${eventId}/upload-participant-letter-template`, fd);
      toast.success("Sjabloon geüpload");
      onChanged({ participant_letter_template_url: data.participant_letter_template_url });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Sjabloon verwijderen? De deelnemersbrief wordt dan niet meer verstuurd totdat je een nieuw sjabloon uploadt.")) return;
    setRemoving(true);
    try {
      await authAxios.delete(`/admin/events/${eventId}/participant-letter-template`);
      toast.success("Sjabloon verwijderd");
      onChanged({ participant_letter_template_url: null });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setRemoving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/participant-letter-preview.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Voorbeeld kon niet worden gegenereerd");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-svj-cyan" />
        <div className="text-sm font-semibold text-white uppercase tracking-wider">Deelnemersbrief</div>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Upload een Word-sjabloon (.docx) met mail-merge-velden — bij een bevestigde bestelling wordt dit automatisch
        per deelnemer ingevuld en als PDF-bijlage meegestuurd met de bevestigingsmail.
      </p>

      <button
        type="button"
        onClick={toggle}
        disabled={toggling}
        data-testid="participant-letter-toggle"
        className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors disabled:opacity-60 ${
          enabled ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan" : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
        }`}
      >
        {toggling ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
        {enabled ? "Deelnemersbrief wordt meegestuurd" : "Zet deelnemersbrief aan"}
      </button>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={fileInputRef} type="file" accept=".docx" onChange={onFile} className="hidden" data-testid="participant-letter-input" />
        {templateUrl ? (
          <>
            <span className="text-xs text-slate-400">Sjabloon geüpload.</span>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="participant-letter-replace"
              className="inline-flex items-center gap-2 rounded-full border-2 border-svj-cyan text-svj-cyan font-bold py-1.5 px-4 text-xs hover:bg-svj-cyan/10 transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Vervangen
            </button>
            <button
              type="button"
              onClick={preview}
              disabled={previewing}
              data-testid="participant-letter-preview"
              className="inline-flex items-center gap-2 rounded-full border border-svj-border text-slate-200 hover:text-white font-bold py-1.5 px-4 text-xs disabled:opacity-60"
            >
              {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Voorbeeld bekijken
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={removing}
              data-testid="participant-letter-remove"
              className="inline-flex items-center gap-2 rounded-full border-2 border-red-500/60 text-red-400 font-bold py-1.5 px-4 text-xs hover:bg-red-500/10 transition-colors disabled:opacity-60"
            >
              {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Verwijderen
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="participant-letter-upload"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2 px-4 text-xs disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload sjabloon (.docx)
          </button>
        )}
      </div>

      <div className="mt-4 rounded-lg bg-svj-bg/60 border border-svj-border p-3 space-y-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Herkende mail-merge-velden</p>
        <p className="text-[11px] text-slate-400">
          <code className="text-svj-cyan">Naam_deelnemer_voor_en_achternaam</code> (volledige naam) ·{" "}
          <code className="text-svj-cyan">Op_welke_dagen_wilt_u_deelnemen_aan_de</code> (gekozen dagen) ·{" "}
          <code className="text-svj-cyan">Deelnemer</code> (startnummer — als dit veld op een eigen, liggende
          laatste pagina staat, wordt daar automatisch een groot afdrukbaar startnummer getekend in plaats van de
          letterlijke tekst).
        </p>
        <p className="text-[11px] text-slate-500">
          Niet ondersteund: afbeeldingen, geneste tabellen, opsommingslijsten, meer dan 2 secties, mail-merge-velden
          in de kop-/voettekst, voetnoten. Deze onderdelen worden best-effort als platte tekst getoond, maar niet
          gegarandeerd correct weergegeven.
        </p>
      </div>
    </div>
  );
}

export default function AdminEventRegistration({ eventId, authAxios, rawEvent, onEventChanged }) {
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(true);
  const [fields, setFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(true);

  const loadDays = async () => {
    setLoadingDays(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/days`);
      setDays(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingDays(false);
    }
  };

  const loadFields = async () => {
    setLoadingFields(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/registration-fields`);
      setFields(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingFields(false);
    }
  };

  useEffect(() => {
    loadDays();
    loadFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  return (
    <div className="space-y-6">
      <EventDaysManager eventId={eventId} authAxios={authAxios} days={days} loading={loadingDays} onChanged={loadDays} />
      <RegistrationFieldsManager eventId={eventId} authAxios={authAxios} fields={fields} loading={loadingFields} onChanged={loadFields} />
      {rawEvent && (
        <ParticipantLetterManager
          eventId={eventId}
          authAxios={authAxios}
          enabled={!!rawEvent.participant_letter_enabled}
          templateUrl={rawEvent.participant_letter_template_url || null}
          onChanged={onEventChanged}
        />
      )}
    </div>
  );
}
